import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { format, formatDistanceToNow } from 'date-fns'
import { getDogName } from '@/lib/dogNames'
import { ConversionBadge } from '@/components/dashboard/ConversionBadge'
import type { Session, Event, EventType } from '@/types/database'

function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

export default function SessionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [session, setSession] = useState<Session | null>(null)
  const [events, setEvents] = useState<Event[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isConverted, setIsConverted] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentEventIndex, setCurrentEventIndex] = useState(0)

  useEffect(() => {
    if (id) {
      fetchSession()
    }
  }, [id])

  useEffect(() => {
    if (isPlaying && currentEventIndex < events.length - 1) {
      const nextEvent = events[currentEventIndex + 1]
      const currentEvent = events[currentEventIndex]
      const delay = (new Date(nextEvent.timestamp).getTime() - new Date(currentEvent.timestamp).getTime()) / playbackSpeed

      const timer = setTimeout(() => {
        setCurrentEventIndex(i => i + 1)
      }, Math.min(delay, 2000)) // Max 2 seconds between events

      return () => clearTimeout(timer)
    } else if (currentEventIndex >= events.length - 1) {
      setIsPlaying(false)
    }
  }, [isPlaying, currentEventIndex, events, playbackSpeed])

  const fetchSession = async () => {
    if (!id) return
    setIsLoading(true)

    try {
      const { data: sessionData } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id)
        .single()

      if (sessionData) {
        setSession(sessionData)

        const { data: eventsData } = await supabase
          .from('events')
          .select('*')
          .eq('session_id', id)
          .order('timestamp', { ascending: true })

        // Filter out heartbeat events from display
        const evts = ((eventsData || []) as Event[]).filter(e => e.event_type !== 'heartbeat')
        setEvents(evts)

        // Conversion is determined by revenue_events, not form_submit
        // For now, don't mark as converted since revenue features are disabled
        setIsConverted(false)
      }
    } catch (error) {
      console.error('Failed to fetch session:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getEventColor = (type: EventType): string => {
    switch (type) {
      case 'pageview':
        return 'bg-green-500'
      case 'click':
        return 'bg-blue-500'
      case 'scroll':
        return 'bg-purple-500'
      case 'form_start':
      case 'form_submit':
      case 'form_abandon':
        return 'bg-orange-500'
      case 'rage_click':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getEventIcon = (type: EventType) => {
    switch (type) {
      case 'pageview':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )
      case 'click':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
        )
      case 'scroll':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        )
      case 'rage_click':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Session not found</p>
        <Link to="/sessions" className="btn-secondary mt-4">Back to sessions</Link>
      </div>
    )
  }

  // Calculate session duration
  const startTime = new Date(session.started_at)
  const endTime = session.last_seen_at ? new Date(session.last_seen_at) : new Date()
  const durationSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000)

  const dogName = getDogName(session.visitor_hash)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/sessions" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            ← Back to sessions
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{dogName}</h1>
            <ConversionBadge isConverted={isConverted} />
          </div>
          <p className="text-gray-600">
            Started {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })} · {formatDuration(durationSeconds)}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Session info */}
        <div className="lg:col-span-1 space-y-4">
          {/* Visitor Info */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Visitor Info</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Visitor</dt>
                <dd className="text-gray-900 font-medium">{dogName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Location</dt>
                <dd className="text-gray-900 flex items-center gap-1.5">
                  {session.country_code ? (
                    <>
                      <span>{getFlagEmoji(session.country_code)}</span>
                      {session.city ? `${session.city}, ${session.country_code}` : session.country_code}
                    </>
                  ) : (
                    'Unknown'
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Entry Page</dt>
                <dd className="text-gray-900 truncate max-w-[150px]" title={session.entry_page || undefined}>
                  {session.entry_page || '-'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Device Details */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Device Details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Device</dt>
                <dd className="text-gray-900 capitalize">{session.device_type || 'Unknown'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Browser</dt>
                <dd className="text-gray-900">
                  {session.browser || 'Unknown'}
                  {session.browser_version && ` v${session.browser_version}`}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">OS</dt>
                <dd className="text-gray-900">
                  {session.os || 'Unknown'}
                  {session.os_version && ` ${session.os_version}`}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Screen</dt>
                <dd className="text-gray-900">
                  {session.screen_width && session.screen_height
                    ? `${session.screen_width} × ${session.screen_height}`
                    : '-'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Traffic Source */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Traffic Source</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Referrer</dt>
                <dd className="text-gray-900 truncate max-w-[150px]" title={session.referrer || undefined}>
                  {session.referrer || 'Direct'}
                </dd>
              </div>
              {session.utm_source && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">UTM Source</dt>
                  <dd className="text-gray-900">{session.utm_source}</dd>
                </div>
              )}
              {session.utm_medium && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">UTM Medium</dt>
                  <dd className="text-gray-900">{session.utm_medium}</dd>
                </div>
              )}
              {session.utm_campaign && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">UTM Campaign</dt>
                  <dd className="text-gray-900">{session.utm_campaign}</dd>
                </div>
              )}
              {session.utm_term && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">UTM Term</dt>
                  <dd className="text-gray-900">{session.utm_term}</dd>
                </div>
              )}
              {session.utm_content && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">UTM Content</dt>
                  <dd className="text-gray-900">{session.utm_content}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Session Stats */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Session Stats</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Duration</dt>
                <dd className="text-gray-900">{formatDuration(durationSeconds)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Page views</dt>
                <dd className="text-gray-900">{session.pageview_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Total events</dt>
                <dd className="text-gray-900">{session.event_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Max scroll</dt>
                <dd className="text-gray-900">{session.max_scroll_depth}%</dd>
              </div>
              {session.has_rage_clicks && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Rage clicks</dt>
                  <dd className="badge-danger">Detected</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Playback controls */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Playback</h3>
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => {
                  if (isPlaying) {
                    setIsPlaying(false)
                  } else {
                    if (currentEventIndex >= events.length - 1) {
                      setCurrentEventIndex(0)
                    }
                    setIsPlaying(true)
                  }
                }}
                className="btn-primary flex items-center gap-2"
              >
                {isPlaying ? (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pause
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Play
                  </>
                )}
              </button>
              <button
                onClick={() => setCurrentEventIndex(0)}
                className="btn-secondary"
              >
                Reset
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Speed:</span>
              {[1, 2, 4].map((speed) => (
                <button
                  key={speed}
                  onClick={() => setPlaybackSpeed(speed)}
                  className={`px-2 py-1 text-sm rounded ${
                    playbackSpeed === speed
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
            <div className="mt-4 text-sm text-gray-500">
              Event {currentEventIndex + 1} of {events.length}
            </div>
          </div>
        </div>

        {/* Event timeline */}
        <div className="lg:col-span-2">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Event Timeline</h3>
            {events.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No events recorded</p>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    className={`relative pl-8 pb-4 border-l-2 transition-opacity ${
                      index <= currentEventIndex ? 'opacity-100' : 'opacity-40'
                    } ${
                      index === currentEventIndex ? 'border-l-primary-500' : 'border-l-gray-200'
                    }`}
                  >
                    <div
                      className={`absolute left-[-9px] top-0 w-4 h-4 rounded-full flex items-center justify-center text-white ${getEventColor(event.event_type)}`}
                    >
                      {getEventIcon(event.event_type)}
                    </div>
                    <div className={`p-3 rounded-lg ${index === currentEventIndex ? 'bg-primary-50 ring-2 ring-primary-200' : 'bg-gray-50'}`}>
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-medium text-gray-900 capitalize">
                          {event.event_type.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(event.timestamp), 'HH:mm:ss')}
                        </span>
                      </div>
                      {event.path && (
                        <p className="text-sm text-gray-600 truncate">{event.path}</p>
                      )}
                      {event.page_title && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{event.page_title}</p>
                      )}
                      {event.properties && Object.keys(event.properties as object).length > 0 && (
                        <div className="mt-2 text-xs text-gray-500 bg-white rounded p-2">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(event.properties, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
